/**
 * MerchantSettlement.js
 *
 * Standalone Settlement component for the Merchant Dashboard.
 *
 * API  : POST /apiwallet/api/services/MerchantSettlement
 * Auth : Bearer token (MerchantToken from sessionStorage)
 *
 * Request fields:
 *   ReqType      = "TAMerchantSettlementDeatils"
 *   FromDate     = user selects (YYYY-MM-DD)
 *   ToDate       = user selects (YYYY-MM-DD)
 *   Mobilenumber = sessionStorage "mobileNumber"
 *   SessionID    = sessionStorage "SessionID"
 *   MachineID    = useIPAddress()
 *   EntityID     = Const entity_id
 *   Platform     = Const Platform
 *   GeoLocation  = Const location
 *
 * Response shape:
 *   { Response_Code, Message, Info: { Table: [ { MerchantID, Name,
 *     SettlementAmount, SettlementStatus, SettledOn,
 *     SettlementRemarks, SettlementUTR } ] } }
 */

import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon         from "@mui/icons-material/Search";
import CalendarTodayIcon  from "@mui/icons-material/CalendarToday";
import FileDownloadIcon   from "@mui/icons-material/FileDownload";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import RefreshIcon        from "@mui/icons-material/Refresh";
import { baseapi }        from "../Environmentalurl";
import { entity_id, location, Platform } from "../Utils/Const";
import useIPAddress       from "../Utils/useIPAddress";
import api                from "../Utils/Api";

// ── Palette (same as MerchantDashboard) ───────────────────────────────────────
const C = {
  accent:    "#6366F1",
  accentSoft:"#EEF2FF",
  green:     "#059669",
  greenSoft: "#D1FAE5",
  amber:     "#D97706",
  amberSoft: "#FEF3C7",
  red:       "#DC2626",
  redSoft:   "#FEE2E2",
  bg:        "#F8FAFC",
  border:    "#E2E8F0",
  muted:     "#64748B",
  text:      "#1E293B",
  surface:   "#FFFFFF",
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n) =>
  `₹${parseFloat(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const today      = () => new Date().toISOString().split("T")[0];
const monthAgo   = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().split("T")[0];
};

const StatusChip = ({ status }) => {
  const map = {
    SETTLED:   { label: "Settled",   bg: C.greenSoft, color: C.green },
    UNSETTLED: { label: "Unsettled", bg: C.amberSoft, color: C.amber },
    PENDING:   { label: "Pending",   bg: C.amberSoft, color: C.amber },
    FAILED:    { label: "Failed",    bg: C.redSoft,   color: C.red   },
  };
  const key = (status || "").toUpperCase();
  const s   = map[key] || { label: status || "Unsettled", bg: C.amberSoft, color: C.amber };
  return (
    <Chip
      label={s.label}
      size="small"
      sx={{
        background: s.bg, color: s.color,
        fontWeight: 700, fontSize: 11,
        height: 22, borderRadius: "6px",
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
};

// ── Component ─────────────────────────────────────────────────────────────────
export default function MerchantSettlement() {
  const ip = useIPAddress();

  // ── Read session values ────────────────────────────────────────────────────
  const mobile    = sessionStorage.getItem("mobileNumber") || "";
  const sessionId = (() => {
    try { return JSON.parse(sessionStorage.getItem("SessionID")) || ""; }
    catch { return sessionStorage.getItem("SessionID") || ""; }
  })();
  const merchantToken = sessionStorage.getItem("MerchantToken") || "";

  // ── Date state ─────────────────────────────────────────────────────────────
  const [fromDate, setFromDate] = useState(monthAgo());
  const [toDate,   setToDate]   = useState(today());
  const [dateError, setDateError] = useState("");

  // ── Data state ─────────────────────────────────────────────────────────────
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false); // has user fetched at least once?
  const [apiMsg,  setApiMsg]  = useState("");
  const [search,  setSearch]  = useState("");

  // ── Summary state (computed from rows) ────────────────────────────────────
  const [summary, setSummary] = useState({
    total:     0,
    settled:   0,
    unsettled: 0,
    count:     0,
  });

  // ── Validate dates ─────────────────────────────────────────────────────────
  const validateDates = () => {
    if (!fromDate) { setDateError("Please select From Date"); return false; }
    if (!toDate)   { setDateError("Please select To Date");   return false; }
    if (fromDate > toDate) {
      setDateError("From Date cannot be after To Date");
      return false;
    }
    setDateError("");
    return true;
  };

  // ── Fetch settlements ──────────────────────────────────────────────────────
  const fetchSettlements = async () => {
    if (!validateDates()) return;

    setLoading(true);
    setApiMsg("");
    setRows([]);

    try {
      const response = await api.post(
        `${baseapi}/MerchantSettlement`,
        {
          ReqType:      "TAMerchantSettlementDeatils",   // exact ReqType from doc
          GeoLocation:  location,
          MachineID:    ip,
          EntityID:     entity_id,
          Mobilenumber: mobile,
          Platform:     Platform,
          FromDate:     fromDate,   // user-selected YYYY-MM-DD
          ToDate:       toDate,     // user-selected YYYY-MM-DD
          SessionID:    sessionId,
        },
        {
          // ✅ Bearer token from sessionStorage (stored after 5555 OTP response)
          headers: {
            Authorization: `Bearer ${merchantToken}`,
          },
        },
      );

      const data = response.data;

      if (data.Response_Code === "1000" && data.Info?.Table) {
        const tableRows = data.Info.Table;
        setRows(tableRows);
        setFetched(true);

        // Compute summary from actual data
        const total     = tableRows.reduce((s, r) => s + (parseFloat(r.SettlementAmount) || 0), 0);
        const settled   = tableRows
          .filter((r) => r.SettlementStatus?.toUpperCase() === "SETTLED" || r.SettledOn)
          .reduce((s, r) => s + (parseFloat(r.SettlementAmount) || 0), 0);
        const unsettled = total - settled;

        setSummary({
          total:     total.toFixed(2),
          settled:   settled.toFixed(2),
          unsettled: unsettled.toFixed(2),
          count:     tableRows.length,
        });

        // ✅ Store in sessionStorage so MerchantDashboard stat cards
        // can read the latest settled/unsettled amounts immediately.
        // sessionStorage doesn't fire events within the same tab,
        // so we dispatch a custom event to notify Dashboard to re-read.
        sessionStorage.setItem("merchantSettledAmount",   settled.toFixed(2));
        sessionStorage.setItem("merchantUnsettledAmount", unsettled.toFixed(2));
        sessionStorage.setItem("merchantTotalSettlement", total.toFixed(2));
        sessionStorage.setItem("merchantSettlementCount", String(tableRows.length));
        window.dispatchEvent(new Event("merchantSettlementUpdated"));
      } else {
        setApiMsg(data.Message || "No settlements found for selected date range.");
        setFetched(true);
        setSummary({ total: 0, settled: 0, unsettled: 0, count: 0 });
      }
    } catch (err) {
      console.error("MerchantSettlement fetch error:", err);
      setApiMsg("Failed to fetch settlements. Please try again.");
      setFetched(true);
    } finally {
      setLoading(false);
    }
  };

  // ── Filtered rows ──────────────────────────────────────────────────────────
  const filtered = rows.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      r.Name?.toLowerCase().includes(q)             ||
      r.SettlementRemarks?.toLowerCase().includes(q)||
      r.SettlementUTR?.toLowerCase().includes(q)    ||
      String(r.SettlementAmount || "").includes(q)
    );
  });

  // ── CSV Export ─────────────────────────────────────────────────────────────
  const handleExport = () => {
    if (!rows.length) return;
    const headers = ["Merchant ID","Name","Amount","Status","Settled On","Remarks","UTR"];
    const csvRows = rows.map((r) => [
      r.MerchantID,
      r.Name,
      r.SettlementAmount,
      r.SettlementStatus || "Unsettled",
      r.SettledOn || "—",
      `"${(r.SettlementRemarks || "").trim()}"`,
      r.SettlementUTR || "—",
    ].join(","));
    const csv  = [headers.join(","), ...csvRows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `settlements_${fromDate}_${toDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box>

      {/* ── Date Filter Card ──────────────────────────────────────────────── */}
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${C.border}`,
          borderRadius: "16px",
          p: 2.5,
          mb: 2.5,
          background: C.surface,
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.text, mb: 2 }}>
          Settlement History
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "flex-end",
          }}
        >
          {/* From Date */}
          <Box sx={{ flex: "1 1 180px", minWidth: 160 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.muted, mb: 0.8 }}>
              From Date
            </Typography>
            <TextField
              type="date"
              size="small"
              fullWidth
              value={fromDate}
              onChange={(e) => { setFromDate(e.target.value); setDateError(""); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarTodayIcon sx={{ fontSize: 15, color: C.muted }} />
                  </InputAdornment>
                ),
                sx: { borderRadius: "10px", fontSize: 13, height: 40 },
              }}
              inputProps={{ max: today() }}
            />
          </Box>

          {/* To Date */}
          <Box sx={{ flex: "1 1 180px", minWidth: 160 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.muted, mb: 0.8 }}>
              To Date
            </Typography>
            <TextField
              type="date"
              size="small"
              fullWidth
              value={toDate}
              onChange={(e) => { setToDate(e.target.value); setDateError(""); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarTodayIcon sx={{ fontSize: 15, color: C.muted }} />
                  </InputAdornment>
                ),
                sx: { borderRadius: "10px", fontSize: 13, height: 40 },
              }}
              inputProps={{ max: today() }}
            />
          </Box>

          {/* Fetch Button */}
          <Button
            variant="contained"
            onClick={fetchSettlements}
            disabled={loading}
            startIcon={
              loading
                ? <CircularProgress size={16} sx={{ color: "#fff" }} />
                : <RefreshIcon sx={{ fontSize: 18 }} />
            }
            sx={{
              borderRadius: "10px",
              height: 40,
              textTransform: "none",
              fontWeight: 700,
              fontSize: 13,
              px: 3,
              background: loading
                ? "#e5e7eb"
                : "linear-gradient(135deg, #6366F1, #4F46E5)",
              boxShadow: loading ? "none" : "0 4px 12px rgba(99,102,241,0.35)",
              "&:hover": { background: "linear-gradient(135deg, #4F46E5, #4338CA)" },
            }}
          >
            {loading ? "Fetching..." : "Fetch Settlements"}
          </Button>
        </Box>

        {/* Date validation error */}
        {dateError && (
          <Typography sx={{ fontSize: 12, color: C.red, mt: 1 }}>
            ⚠ {dateError}
          </Typography>
        )}
      </Card>

      {/* ── Summary Cards (shown after first fetch) ───────────────────────── */}
      {fetched && rows.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: 2,
            mb: 2.5,
          }}
        >
          {[
            { label: "Total Amount",     value: fmt(summary.total),     color: C.accent  },
            { label: "Settled",          value: fmt(summary.settled),   color: C.green   },
            { label: "Unsettled",        value: fmt(summary.unsettled), color: C.amber   },
            { label: "Total Records",    value: summary.count,          color: C.text    },
          ].map((s) => (
            <Card
              key={s.label}
              elevation={0}
              sx={{
                border: `1px solid ${C.border}`,
                borderRadius: "14px",
                p: 2,
                background: C.surface,
              }}
            >
              <Typography sx={{ fontSize: 11, color: C.muted, fontWeight: 600, mb: 0.5 }}>
                {s.label}
              </Typography>
              <Typography sx={{ fontSize: 20, fontWeight: 800, color: s.color }}>
                {s.value}
              </Typography>
            </Card>
          ))}
        </Box>
      )}

      {/* ── Results Table ─────────────────────────────────────────────────── */}
      {fetched && (
        <Card
          elevation={0}
          sx={{
            border: `1px solid ${C.border}`,
            borderRadius: "16px",
            overflow: "hidden",
          }}
        >
          {/* Table toolbar */}
          <Box
            sx={{
              px: 2.5, py: 1.8,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
              borderBottom: `1px solid ${C.border}`,
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                Settlements
                <Typography
                  component="span"
                  sx={{ ml: 1, fontSize: 11, color: C.muted, fontWeight: 400 }}
                >
                  {fromDate} → {toDate}
                </Typography>
              </Typography>
            </Box>

            <Box display="flex" gap={1} alignItems="center">
              {/* Search */}
              <TextField
                size="small"
                placeholder="Search name, UTR, remarks..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 15, color: C.muted }} />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: "10px", fontSize: 13, height: 36 },
                }}
                sx={{ width: 240 }}
              />

              {/* Export */}
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadIcon sx={{ fontSize: 15 }} />}
                onClick={handleExport}
                disabled={!rows.length}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: 12,
                  height: 36,
                  borderColor: C.border,
                  color: C.muted,
                  "&:hover": { borderColor: C.accent, color: C.accent },
                }}
              >
                Export CSV
              </Button>
            </Box>
          </Box>

          {/* Table */}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: C.bg }}>
                  {[
                    "Merchant Name",
                    "Settlement Amount",
                    "Settlement Remarks",
                    "UTR Number",
                    "Settled On",
                    "Status",
                  ].map((h) => (
                    <TableCell
                      key={h}
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: C.muted,
                        py: 1.5,
                        letterSpacing: 0.4,
                        textTransform: "uppercase",
                        borderBottom: `1px solid ${C.border}`,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {/* API error / no results */}
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <AccountBalanceIcon sx={{ fontSize: 40, color: C.border, mb: 1, display: "block", mx: "auto" }} />
                      <Typography sx={{ fontSize: 14, color: C.muted, fontWeight: 600 }}>
                        {apiMsg || "No settlements found for this date range"}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: C.muted, mt: 0.5 }}>
                        Try adjusting the From / To dates and fetch again
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography sx={{ fontSize: 13, color: C.muted }}>
                        No results match your search
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((r, i) => (
                    <TableRow
                      key={`${r.MerchantID}-${i}`}
                      sx={{
                        "&:hover": { background: "#F8FAFF" },
                        borderBottom: `1px solid ${C.border}`,
                      }}
                    >
                      {/* Merchant Name + ID */}
                      <TableCell sx={{ py: 1.8 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                          {r.Name || "—"}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: C.muted }}>
                          ID: {r.MerchantID || "—"}
                        </Typography>
                      </TableCell>

                      {/* Settlement Amount */}
                      <TableCell sx={{ py: 1.8 }}>
                        <Typography sx={{ fontSize: 15, fontWeight: 800, color: C.green }}>
                          {fmt(r.SettlementAmount)}
                        </Typography>
                      </TableCell>

                      {/* Settlement Remarks */}
                      <TableCell sx={{ py: 1.8, maxWidth: 220 }}>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: C.text,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={r.SettlementRemarks?.trim() || ""}
                        >
                          {r.SettlementRemarks?.trim() || "—"}
                        </Typography>
                      </TableCell>

                      {/* UTR Number */}
                      <TableCell sx={{ py: 1.8 }}>
                        <Typography
                          sx={{
                            fontSize: 12,
                            fontFamily: "monospace",
                            fontWeight: 600,
                            color: r.SettlementUTR ? C.accent : C.muted,
                          }}
                        >
                          {r.SettlementUTR || "Pending"}
                        </Typography>
                      </TableCell>

                      {/* Settled On */}
                      <TableCell sx={{ py: 1.8 }}>
                        <Typography sx={{ fontSize: 12, color: C.text }}>
                          {r.SettledOn || "—"}
                        </Typography>
                      </TableCell>

                      {/* Status */}
                      <TableCell sx={{ py: 1.8 }}>
                        <StatusChip
                          status={
                            r.SettlementStatus ||
                            (r.SettledOn ? "SETTLED" : "UNSETTLED")
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Footer */}
          <Box
            sx={{
              px: 2.5, py: 1.5,
              background: C.bg,
              borderTop: `1px solid ${C.border}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontSize: 12, color: C.muted }}>
              {filtered.length} of {rows.length} records
            </Typography>
            <Typography sx={{ fontSize: 12, color: C.muted }}>
              Session: {mobile}
            </Typography>
          </Box>
        </Card>
      )}

      {/* ── Initial state (before first fetch) ───────────────────────────── */}
      {!fetched && !loading && (
        <Card
          elevation={0}
          sx={{
            border: `1px dashed ${C.border}`,
            borderRadius: "16px",
            p: 5,
            textAlign: "center",
            background: C.bg,
          }}
        >
          <AccountBalanceIcon sx={{ fontSize: 48, color: C.border, mb: 1.5 }} />
          <Typography sx={{ fontSize: 15, fontWeight: 600, color: C.muted, mb: 0.5 }}>
            Select date range and fetch settlements
          </Typography>
          <Typography sx={{ fontSize: 12, color: C.muted }}>
            Choose From Date and To Date above, then click "Fetch Settlements"
          </Typography>
        </Card>
      )}
    </Box>
  );
}